import React, { useEffect, useState } from 'react';
import styles from './TaskChat.module.scss';
import api from '../../services/api';
import MentionTextarea from '../MentionTextarea/MentionTextarea';
import MediaViewer from '../MediaViewer/MediaViewer';
import { hasActiveTextSelection } from '../../utils/textSelection';
import SafeMessageBody from '../../utils/SafeMessageBody';
import AssistantReplyFeedback from '../AssistantReplyFeedback/AssistantReplyFeedback';
import DesignLayoutPreview from '../DesignLayoutPreview/DesignLayoutPreview';

function normalizeTemplateSearch(value) {
    return String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function matchesTemplateTitle(template, query) {
    const normalizedQuery = normalizeTemplateSearch(query);
    if (!normalizedQuery) {
        return true;
    }

    const title = normalizeTemplateSearch(template?.title);
    if (!title) {
        return false;
    }

    return normalizedQuery
        .split(' ')
        .filter(Boolean)
        .every((token) => title.includes(token));
}

function normalizeMentions(value) {
    if (!Array.isArray(value)) {
        return [];
    }

    const unique = new Map();
    value.forEach((item) => {
        const id = Number(item?.id || 0);
        const label = String(item?.label || '').trim();
        if (id > 0 && label) {
            unique.set(id, { id, label });
        }
    });

    return Array.from(unique.values());
}

function resolveFileType(link, type) {
    const normalizedType = String(type || '').toLowerCase();
    if (['image', 'video', 'audio', 'file'].includes(normalizedType)) {
        return normalizedType;
    }

    const extension = String(link || '').split('?')[0].split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'oga', 'm4a'].includes(extension)) return 'audio';
    return 'file';
}

function getFileName(link, fallback = 'Файл') {
    try {
        const pathname = new URL(String(link || ''), window.location.origin).pathname;
        const rawName = pathname.split('/').pop() || fallback;
        return decodeURIComponent(rawName);
    } catch {
        const rawName = String(link || '').split('/').pop() || fallback;
        return decodeURIComponent(rawName);
    }
}

function areMessageFilesEqual(prevFiles, nextFiles) {
    const left = Array.isArray(prevFiles) ? prevFiles : [];
    const right = Array.isArray(nextFiles) ? nextFiles : [];

    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
        const prevFile = left[i] || {};
        const nextFile = right[i] || {};
        if ((prevFile.link || '') !== (nextFile.link || '')) return false;
        if ((prevFile.type || '') !== (nextFile.type || '')) return false;
    }

    return true;
}

function areMessagesEqual(prevMessages, nextMessages) {
    const left = Array.isArray(prevMessages) ? prevMessages : [];
    const right = Array.isArray(nextMessages) ? nextMessages : [];

    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
        const prevMessage = left[i] || {};
        const nextMessage = right[i] || {};

        if ((prevMessage.id || null) !== (nextMessage.id || null)) return false;
        if ((prevMessage.text || '') !== (nextMessage.text || '')) return false;
        if ((prevMessage.textMarkup || '') !== (nextMessage.textMarkup || '')) return false;
        if (JSON.stringify(prevMessage.aiReply) !== JSON.stringify(nextMessage.aiReply)) return false;
        if ((prevMessage.author || '') !== (nextMessage.author || '')) return false;
        if ((prevMessage.createdAt || '') !== (nextMessage.createdAt || '')) return false;
        if (Boolean(prevMessage.isInternal) !== Boolean(nextMessage.isInternal)) return false;
        if (JSON.stringify(normalizeMentions(prevMessage.mentions)) !== JSON.stringify(normalizeMentions(nextMessage.mentions))) return false;
        if (!areMessageFilesEqual(prevMessage.files, nextMessage.files)) return false;
    }

    return true;
}

function upsertMessage(prevMessages, nextMessage) {
    const messages = Array.isArray(prevMessages) ? prevMessages : [];
    if (!nextMessage) {
        return messages;
    }

    const nextId = nextMessage.id ?? null;
    if (nextId === null) {
        return [...messages, nextMessage];
    }

    const existingIndex = messages.findIndex((message) => (message?.id ?? null) === nextId);
    if (existingIndex === -1) {
        return [...messages, nextMessage];
    }

    return messages.map((message) => ((message?.id ?? null) === nextId ? nextMessage : message));
}

const TaskChat = ({
    taskId,
    templatesResponse,
    onMessagesChanged,
    mentionCandidates = [],
}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [newMessageMentions, setNewMessageMentions] = useState([]);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editedText, setEditedText] = useState('');
    const [editedMentions, setEditedMentions] = useState([]);
    const [editedIsInternal, setEditedIsInternal] = useState(false);
    const [messageToDelete, setMessageToDelete] = useState(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [templateSearchQuery, setTemplateSearchQuery] = useState('');
    const [templateApplyError, setTemplateApplyError] = useState('');
    const hasLoadedMessagesRef = React.useRef(false);
    const messagesRef = React.useRef([]);
    const messagesContainerRef = React.useRef(null);
    const templateSearchInputRef = React.useRef(null);
    const pendingScrollRef = React.useRef(null);
    const pollTimeoutRef = React.useRef(null);
    const isFetchingMessagesRef = React.useRef(false);
    const isSendingMessageRef = React.useRef(false);
    const draftHydratedRef = React.useRef(false);

    const draftStorageKey = taskId ? `support-task-chat-draft:${taskId}` : null;

    useEffect(() => {
        draftHydratedRef.current = false;
        if (!draftStorageKey) {
            setNewMessage('');
            setNewMessageMentions([]);
            draftHydratedRef.current = true;
            return;
        }

        try {
            const draft = JSON.parse(localStorage.getItem(draftStorageKey) || '{}');
            setNewMessage(String(draft.text || ''));
            setNewMessageMentions(normalizeMentions(draft.mentions));
        } catch {
            setNewMessage('');
            setNewMessageMentions([]);
        } finally {
            draftHydratedRef.current = true;
        }
    }, [draftStorageKey]);

    useEffect(() => {
        if (!draftStorageKey || !draftHydratedRef.current) {
            return;
        }

        const hasDraft = newMessage.trim() !== '' || normalizeMentions(newMessageMentions).length > 0;
        if (!hasDraft) {
            localStorage.removeItem(draftStorageKey);
            return;
        }

        localStorage.setItem(draftStorageKey, JSON.stringify({
            text: newMessage,
            mentions: normalizeMentions(newMessageMentions),
            updatedAt: new Date().toISOString(),
        }));
    }, [draftStorageKey, newMessage, newMessageMentions]);

    const captureScrollState = (mode = 'preserve') => {
        const node = messagesContainerRef.current;
        if (!node) {
            pendingScrollRef.current = null;
            return;
        }

        const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
        pendingScrollRef.current = {
            mode,
            distanceFromBottom,
            wasNearBottom: distanceFromBottom <= 24,
        };
    };

    const updateMessages = (nextValue, options = {}) => {
        if (options.preserveScroll) {
            captureScrollState(options.scrollMode || 'preserve');
        }

        setMessages((prevMessages) => {
            const resolvedMessages = typeof nextValue === 'function' ? nextValue(prevMessages) : nextValue;
            messagesRef.current = resolvedMessages;
            return resolvedMessages;
        });
    };

    React.useLayoutEffect(() => {
        const node = messagesContainerRef.current;
        const pendingScroll = pendingScrollRef.current;

        if (!node || !pendingScroll) {
            return;
        }

        if (pendingScroll.mode === 'bottom' || pendingScroll.wasNearBottom) {
            node.scrollTop = node.scrollHeight;
        } else {
            node.scrollTop = Math.max(
                0,
                node.scrollHeight - node.clientHeight - pendingScroll.distanceFromBottom
            );
        }

        pendingScrollRef.current = null;
    }, [messages]);

    const fetchMessages = async () => {
        if (isFetchingMessagesRef.current) {
            return;
        }

        if (hasLoadedMessagesRef.current && hasActiveTextSelection()) {
            return;
        }

        isFetchingMessagesRef.current = true;

        try {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await api.get(`/api/tasks/${taskId}/messages?timeZone=${userTimeZone}`);
            const nextMessages = Array.isArray(response.data) ? response.data : [];
            if (!areMessagesEqual(messagesRef.current, nextMessages)) {
                updateMessages(nextMessages, {
                    preserveScroll: hasLoadedMessagesRef.current,
                    scrollMode: 'preserve',
                });
            }
            hasLoadedMessagesRef.current = true;
        } catch (error) {
            console.error('Ошибка при загрузке сообщений:', error);
        } finally {
            isFetchingMessagesRef.current = false;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const clearPollTimeout = () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };

        const scheduleNextPoll = () => {
            clearPollTimeout();
            if (cancelled) {
                return;
            }
            pollTimeoutRef.current = setTimeout(() => {
                void runPollCycle();
            }, 7000);
        };

        const runPollCycle = async () => {
            if (cancelled) {
                return;
            }

            if (document.visibilityState === 'hidden') {
                scheduleNextPoll();
                return;
            }

            await fetchMessages();
            scheduleNextPoll();
        };

        hasLoadedMessagesRef.current = false;
        isFetchingMessagesRef.current = false;
        messagesRef.current = [];
        setMessages([]);

        void (async () => {
            await fetchMessages();
            scheduleNextPoll();
        })();

        return () => {
            cancelled = true;
            clearPollTimeout();
        };
    }, [taskId]);

    useEffect(() => {
        if (!isTemplateModalOpen) {
            return undefined;
        }

        const focusTimer = window.setTimeout(() => {
            templateSearchInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [isTemplateModalOpen]);

    const openTemplateModal = () => {
        setTemplateSearchQuery('');
        setIsTemplateModalOpen(true);
    };
    const closeTemplateModal = () => setIsTemplateModalOpen(false);

    const handleTemplateSelect = async (template) => {
        setNewMessage(template.description);
        setNewMessageMentions([]);
        setTemplateApplyError('');
        closeTemplateModal();

        const patch = {};
        const nextTitle = String(template?.taskTitleAfterApply || '').trim();
        const nextStatus = String(template?.taskStatusAfterApply?.title || '').trim();

        if (nextTitle) {
            patch.title = nextTitle;
        }
        if (nextStatus) {
            patch.stage = nextStatus;
        }
        if (Object.keys(patch).length === 0 || !taskId) {
            return;
        }

        try {
            await api.patch(`/tasks/${taskId}`, patch);
            await onMessagesChanged?.();
        } catch (error) {
            console.error('Ошибка применения авто-действий шаблона:', error);
            setTemplateApplyError('Не удалось применить авто-действия шаблона.');
        }
    };

    const stopEditing = () => {
        setEditingMessageId(null);
        setEditedText('');
        setEditedMentions([]);
        setEditedIsInternal(false);
    };

    const startEditing = (message) => {
        setEditingMessageId(message.id);
        setEditedText(message.text || '');
        setEditedMentions(normalizeMentions(message.mentions));
        setEditedIsInternal(Boolean(message.isInternal));
    };

    const handleSendMessage = async () => {
        if (isSendingMessageRef.current || isLoading) return;
        if (!newMessage.trim() && files.length === 0) return;

        isSendingMessageRef.current = true;
        setIsLoading(true);

        try {
            const draftMessage = newMessage;
            const draftFiles = [...files];
            let fileUrls = [];

            if (draftFiles.length > 0) {
                const uploadPromises = draftFiles.map(async (file, index) => {
                    const formData = new FormData();
                    formData.append('file', file);

                    setUploadProgress((prev) => ({ ...prev, [index]: 0 }));

                    const uploadResponse = await api.post('/uploadFile', formData, {
                        onUploadProgress: (progressEvent) => {
                            const total = progressEvent.total || 0;
                            if (!total) return;
                            const percent = Math.round((progressEvent.loaded * 100) / total);
                            setUploadProgress((prev) => ({ ...prev, [index]: percent }));
                        },
                    });

                    setUploadProgress((prev) => ({ ...prev, [index]: 100 }));
                    return uploadResponse?.data?.url;
                });

                fileUrls = (await Promise.all(uploadPromises)).filter(Boolean);
            }

            const messageResponse = await api.post(`/api/tasks/${taskId}/messages`, {
                text: draftMessage,
                mentions: newMessageMentions,
                isInternal: false,
                files: fileUrls,
            });

            const newMessageData = messageResponse.data;
            updateMessages((prevMessages) => upsertMessage(prevMessages, newMessageData), {
                preserveScroll: true,
                scrollMode: 'bottom',
            });
            setNewMessage('');
            setNewMessageMentions([]);
            if (draftStorageKey) {
                localStorage.removeItem(draftStorageKey);
            }
            setFiles([]);
            setUploadProgress({});
            await onMessagesChanged?.();
        } catch (error) {
            console.error('Ошибка при отправке сообщения:', error);
        } finally {
            isSendingMessageRef.current = false;
            setIsLoading(false);
        }
    };

    const handleComposerKeyDown = (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void handleSendMessage();
        }
    };

    const handleEditComposerKeyDown = (messageId) => (event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            void handleEditMessage(messageId);
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length > 0) {
            setFiles((prev) => [...prev, ...selectedFiles]);
        }
    };

    const handlePaste = (e) => {
        const clipboardItems = Array.from(e.clipboardData?.items || []);
        const clipboardFiles = clipboardItems
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter(Boolean);

        if (clipboardFiles.length === 0) return;

        const preparedFiles = clipboardFiles.map((file, index) => {
            if (file.name && file.name !== '') return file;
            const ext = file.type?.split('/')[1] || 'bin';
            const name = `clipboard-${Date.now()}-${index}.${ext}`;
            return new File([file], name, { type: file.type || 'application/octet-stream' });
        });

        setFiles((prev) => [...prev, ...preparedFiles]);
        e.preventDefault();
    };

    const handleRemoveFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setUploadProgress((prev) => {
            const updatedProgress = { ...prev };
            delete updatedProgress[index];
            return updatedProgress;
        });
    };

    const handleEditMessage = async (messageId) => {
        try {
            const response = await api.put(`/messages/${messageId}`, {
                text: editedText,
                mentions: editedMentions,
                isInternal: editedIsInternal,
            });

            const updatedMessage = response.data;
            updateMessages((prevMessages) => (
                prevMessages.map((msg) => (msg.id === updatedMessage.id ? updatedMessage : msg))
            ), {
                preserveScroll: true,
                scrollMode: 'preserve',
            });
            stopEditing();
            await onMessagesChanged?.();
        } catch (error) {
            console.error('Ошибка:', error);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        try {
            await api.delete(`/messages/${messageId}`);
            updateMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== messageId), {
                preserveScroll: true,
                scrollMode: 'preserve',
            });
            setMessageToDelete(null);
            await onMessagesChanged?.();
        } catch (error) {
            console.error('Ошибка:', error);
        }
    };

    const templateItems = Array.isArray(templatesResponse) ? templatesResponse : [];
    const filteredTemplateItems = templateItems.filter((template) => matchesTemplateTitle(template, templateSearchQuery));
    const normalizedCandidates = normalizeMentions(mentionCandidates);

    return (
        <div className={styles.chatContainer}>
            {isTemplateModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <h3>Выберите шаблон</h3>
                        <input
                            ref={templateSearchInputRef}
                            type="search"
                            value={templateSearchQuery}
                            onChange={(event) => setTemplateSearchQuery(event.target.value)}
                            className={styles.templateSearchInput}
                            placeholder="Поиск по названию"
                        />
                        <div className={styles.templateListContainer}>
                            {filteredTemplateItems.length > 0 ? (
                                <ul className={styles.templateList}>
                                    {filteredTemplateItems.map((template) => (
                                        <li
                                            key={template.id}
                                            className={styles.templateItem}
                                            onClick={() => void handleTemplateSelect(template)}
                                        >
                                            <strong>{template.title}</strong>
                                            <p>{template.description}</p>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className={styles.templateEmptyState}>Шаблоны не найдены</div>
                            )}
                        </div>
                        <button onClick={closeTemplateModal} className={styles.closeModalButton}>
                            Закрыть
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.chatMessages} ref={messagesContainerRef}>
                {messages.length === 0 ? (
                    <div className={styles.emptyState}>Сообщений пока нет.</div>
                ) : messages.map((message) => {
                    const isOwnMessage = message.author === 'Я';
                    const messageFiles = Array.isArray(message.files) ? message.files : [];
                    const messageMentions = normalizeMentions(message.mentions);

                    return (
                        <div
                            key={message.id}
                            className={`${styles.message} ${
                                isOwnMessage ? styles.myMessage : styles.otherMessage
                            }`}
                        >
                            <div className={styles.messageHeader}>
                                <div className={styles.messageAuthor}>{message.author || 'Сотрудник'}</div>
                                <div className={styles.messageBadges}>
                                    {message.isInternal ? (
                                        <span className={styles.internalBadge}>Внутреннее сообщение</span>
                                    ) : null}
                                    {messageMentions.length > 0 ? (
                                        <span className={styles.mentionsBadge}>
                                            {messageMentions.map((item) => `@${item.label}`).join(', ')}
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            {editingMessageId === message.id ? (
                                <div className={styles.editMessageContainer}>
                                    <MentionTextarea
                                        value={editedText}
                                        onChange={setEditedText}
                                        mentions={editedMentions}
                                        onMentionsChange={setEditedMentions}
                                        candidates={normalizedCandidates}
                                        placeholder="Отредактируйте сообщение..."
                                        disabled={isLoading}
                                        rows={4}
                                        className={styles.editMessageInput}
                                        onKeyDown={handleEditComposerKeyDown(message.id)}
                                    />

                                    <div className={styles.editActions}>
                                        <button
                                            onClick={() => handleEditMessage(message.id)}
                                            className={styles.saveEditButton}
                                        >
                                            Сохранить
                                        </button>
                                        <button
                                            onClick={stopEditing}
                                            className={styles.cancelEditButton}
                                        >
                                            Отмена
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.messageText}>
                                        <SafeMessageBody body={String(message.textMarkup ?? '').trim() !== '' ? message.textMarkup : message.text} />
                                    </div>
                                    <DesignLayoutPreview text={String(message.textMarkup ?? '').trim() !== '' ? message.textMarkup : message.text} />
                                    <AssistantReplyFeedback reply={message.aiReply} endpoint={`/api/tasks/${taskId}/messages/${message.id}/ai-feedback`} />

                                    {messageFiles.length > 0 ? (
                                        <div className={styles.messageFiles}>
                                            {messageFiles.map((fileUrl, index) => {
                                                const fileType = resolveFileType(fileUrl?.link, fileUrl?.type);
                                                const fileName = getFileName(fileUrl?.link, `Файл ${index + 1}`);

                                                return (
                                                    <div key={`${fileUrl?.link || 'file'}-${index}`} className={styles.messageFile}>
                                                        {fileType === 'file' ? (
                                                            <div className={styles.fileAttachment}>
                                                                <span className={styles.messageFileName}>{fileName}</span>
                                                                <a href={fileUrl.link} target="_blank" rel="noreferrer">
                                                                    Скачать файл
                                                                </a>
                                                            </div>
                                                        ) : (
                                                            <div className={styles.previewAttachment}>
                                                                <MediaViewer
                                                                    link={fileUrl.link}
                                                                    type={fileType}
                                                                    alt={fileName}
                                                                    label={fileType === 'video' ? 'Открыть видео' : fileName}
                                                                />
                                                                <div className={styles.messageFileMeta}>
                                                                    <span className={styles.messageFileName}>{fileName}</span>
                                                                    <a href={fileUrl.link} target="_blank" rel="noreferrer">
                                                                        Скачать
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}

                                    <div className={styles.messageFooter}>
                                        <div className={styles.messageTimestamp}>{message.createdAt}</div>
                                        {isOwnMessage ? (
                                            <div className={styles.messageActions}>
                                                <button
                                                    onClick={() => startEditing(message)}
                                                    className={styles.editButton}
                                                >
                                                    Изменить
                                                </button>
                                                <button
                                                    onClick={() => setMessageToDelete(message.id)}
                                                    className={styles.deleteButton}
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className={styles.chatInput}>
                {templateApplyError ? (
                    <div className={styles.templateApplyError}>{templateApplyError}</div>
                ) : null}

                <MentionTextarea
                    value={newMessage}
                    onChange={setNewMessage}
                    mentions={newMessageMentions}
                    onMentionsChange={setNewMessageMentions}
                    candidates={normalizedCandidates}
                    onKeyDown={handleComposerKeyDown}
                    onPaste={handlePaste}
                    placeholder="Введите сообщение..."
                    className={styles.messageInput}
                    disabled={isLoading}
                    rows={4}
                />

                <div className={styles.composerMeta}>
                    <div className={styles.composerActions}>
                        <label className={styles.fileInputLabel}>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                className={styles.fileInput}
                                disabled={isLoading}
                                multiple
                            />
                            Файл
                        </label>

                        <button onClick={openTemplateModal} className={styles.templateButton}>
                            Шаблоны
                        </button>

                    </div>

                    <button onClick={handleSendMessage} className={styles.sendButton} disabled={isLoading}>
                        {isLoading ? 'Отправка...' : 'Отправить'}
                    </button>
                </div>
            </div>

            {files.length > 0 && (
                <div className={styles.attachedFiles}>
                    {files.map((file, index) => (
                        <div key={index} className={styles.attachedFile}>
                            <span>{file.name}</span>
                            {uploadProgress[index] !== undefined && (
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{ width: `${uploadProgress[index]}%` }}
                                    />
                                </div>
                            )}
                            <button onClick={() => handleRemoveFile(index)} className={styles.removeFileButton}>
                                x
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {messageToDelete !== null && (
                <div className={styles.confirmationDialog}>
                    <div className={styles.confirmationContent}>
                        <p>Вы уверены, что хотите удалить это сообщение?</p>
                        <button
                            onClick={() => handleDeleteMessage(messageToDelete)}
                            className={styles.confirmButton}
                        >
                            Удалить
                        </button>
                        <button
                            onClick={() => setMessageToDelete(null)}
                            className={styles.cancelButton}
                        >
                            Отмена
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskChat;
